const STORAGE_KEY = "cmVideoSessionDevicePreferences";
const DEFAULTS_KEY = "cmVideoSessionDeviceDefaults";
const SESSION_KEY = "cmVideoCallSession";

export const saveVideoSessionDevices = (preferences) => {
  if (!preferences) return;

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
};

export const loadVideoSessionDevices = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearVideoSessionDevices = () => {
  sessionStorage.removeItem(STORAGE_KEY);
};

export const saveVideoCallSession = (session) => {
  if (!session) return;

  try {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ ...session, savedAt: Date.now() })
    );
  } catch {
    // Ignore storage quota or privacy mode errors.
  }
};

export const loadVideoCallSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const maxAgeMs = 2 * 60 * 60 * 1000;

    if (parsed?.savedAt && Date.now() - parsed.savedAt > maxAgeMs) {
      clearVideoCallSession();
      return null;
    }

    const { savedAt, ...session } = parsed;
    return session;
  } catch {
    return null;
  }
};

export const clearVideoCallSession = () => {
  localStorage.removeItem(SESSION_KEY);
};

export const openVideoCallTab = (payload) => {
  saveVideoCallSession(payload);

  const newWindow = window.open("/video-call", "_blank");

  if (!newWindow) {
    window.location.assign("/video-call");
    return true;
  }

  const message = { type: "VIDEO_CALL_DATA", data: payload };
  const origin = window.location.origin;

  const send = () => {
    try {
      if (!newWindow.closed) {
        newWindow.postMessage(message, origin);
      }
    } catch {
      // Ignore if the popup is unavailable.
    }
  };

  newWindow.addEventListener("load", send);
  [250, 500, 1000, 2000].forEach((delay) => {
    setTimeout(send, delay);
  });

  return true;
};

export const saveVideoSessionDeviceDefaults = (preferences) => {
  if (!preferences) return;

  try {
    localStorage.setItem(DEFAULTS_KEY, JSON.stringify(preferences));
  } catch {
    // Ignore storage quota or privacy mode errors.
  }
};

export const loadVideoSessionDeviceDefaults = () => {
  try {
    const raw = localStorage.getItem(DEFAULTS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const buildVideoSessionDevices = ({
  cameraId,
  cameraOn,
  micId,
  micOn,
  speakerId,
}) => ({
  cameraId: cameraId || "",
  cameraOn: Boolean(cameraOn),
  micId: micId || "",
  micOn: Boolean(micOn),
  speakerId: speakerId || "",
});

const WAITING_ROOM_SESSION_KEY = "cmWaitingRoomSession";

export const saveWaitingRoomSession = (appointment) => {
  if (!appointment) return;

  try {
    localStorage.setItem(
      WAITING_ROOM_SESSION_KEY,
      JSON.stringify({ appointment, savedAt: Date.now() }),
    );
  } catch {
    // Ignore storage quota or privacy mode errors.
  }
};

export const loadWaitingRoomSession = () => {
  try {
    const raw = localStorage.getItem(WAITING_ROOM_SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const maxAgeMs = 2 * 60 * 60 * 1000;

    if (parsed?.savedAt && Date.now() - parsed.savedAt > maxAgeMs) {
      clearWaitingRoomSession();
      return null;
    }

    return parsed?.appointment || null;
  } catch {
    return null;
  }
};

export const clearWaitingRoomSession = () => {
  localStorage.removeItem(WAITING_ROOM_SESSION_KEY);
};

/**
 * Opens the Care Manager waiting room in a new tab so the schedule/app
 * tab stays available for navigation during the session.
 */
export const openWaitingRoomTab = (appointment) => {
  if (!appointment) return false;

  saveWaitingRoomSession(appointment);

  const newWindow = window.open("/waiting-room", "_blank");

  if (!newWindow) {
    return false;
  }

  const message = {
    type: "WAITING_ROOM_DATA",
    data: { appointment },
  };
  const origin = window.location.origin;

  const send = () => {
    try {
      if (!newWindow.closed) {
        newWindow.postMessage(message, origin);
      }
    } catch {
      // Ignore if the popup is unavailable.
    }
  };

  newWindow.addEventListener("load", send);
  [250, 500, 1000, 2000].forEach((delay) => {
    setTimeout(send, delay);
  });

  return true;
};
